'use client';

import type { Route } from 'next';
import Link from 'next/link';
import Nav from '@/components/Nav';
import { type TranslationKey, useTranslation } from '@/lib/i18n';

/**
 * Landing-page section tile. Titles/descriptions are translation keys so
 * no English text is hardcoded in the data table.
 */
interface HomeSection {
  readonly titleKey: TranslationKey;
  readonly href: Route;
  readonly descriptionKey: TranslationKey;
  readonly icon: string;
}

const SECTIONS: readonly HomeSection[] = [
  {
    titleKey: 'home.section.courses.title',
    href: '/courses',
    descriptionKey: 'home.section.courses.description',
    icon: '📚',
  },
  {
    titleKey: 'home.section.schoolhouse.title',
    href: '/schoolhouse',
    descriptionKey: 'home.section.schoolhouse.description',
    icon: '🏫',
  },
  {
    titleKey: 'home.section.library.title',
    href: '/library',
    descriptionKey: 'home.section.library.description',
    icon: '📖',
  },
  {
    titleKey: 'home.section.assignments.title',
    href: '/assignments',
    descriptionKey: 'home.section.assignments.description',
    icon: '📝',
  },
  {
    titleKey: 'home.section.quizzes.title',
    href: '/quizzes',
    descriptionKey: 'home.section.quizzes.description',
    icon: '✏️',
  },
  {
    titleKey: 'home.section.aiTutor.title',
    href: '/ai-tutor',
    descriptionKey: 'home.section.aiTutor.description',
    icon: '🤖',
  },
] as const;

export default function HomePage() {
  const { t } = useTranslation();
  return (
    <div>
      <Nav />
      <main className='mx-auto max-w-5xl p-8'>
        <h1 className='text-4xl font-bold'>{t('app.title')}</h1>
        <p className='mt-2 text-lg opacity-80'>{t('home.tagline')}</p>
        <div className='mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
          {SECTIONS.map((s) => (
            <Link
              key={s.href}
              href={s.href}
              className='rounded-lg border border-border p-5 transition-colors hover:bg-foreground/5'
            >
              <div className='text-2xl'>{s.icon}</div>
              <h2 className='mt-2 text-lg font-semibold'>{t(s.titleKey)}</h2>
              <p className='mt-1 text-sm opacity-70'>{t(s.descriptionKey)}</p>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}
